/* /training/tool-intros/:slug — Single-tool intro lesson player. */
import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import KineticLesson from "../components/KineticLesson";
import { TOOL_INTROS, TOOL_INTROS_BY_SLUG } from "../data/toolIntros";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

const G   = "#2E9F3A";
const RED = "#C9202E";
const INK = "#0F172A";

export default function ToolIntroPlayerPage() {
  const { slug } = useParams();
  const tool = TOOL_INTROS_BY_SLUG[slug];

  useEffect(() => {
    document.title = tool ? `${tool.name} — Mini Lesson` : "Mini Lesson — Faitharama";
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug, tool]);

  if (!tool) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <SiteNav />
        <main className="flex-1 flex items-center justify-center px-5">
          <div className="text-center">
            <h1 className="font-serif-display text-3xl uppercase mb-3">Lesson not found.</h1>
            <Link to="/training/tool-intros" className="underline font-bold" style={{ color: G }}>
              Back to all mini lessons
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  /* Prev/next navigation through the 12 lessons. */
  const idx  = TOOL_INTROS.findIndex((t) => t.slug === slug);
  const prev = idx > 0 ? TOOL_INTROS[idx - 1] : null;
  const next = idx < TOOL_INTROS.length - 1 ? TOOL_INTROS[idx + 1] : null;

  return (
    <div className="min-h-screen bg-white">
      <SiteNav />

      <section className="py-10 md:py-14 bg-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8">

          <Link to="/training/tool-intros"
                data-testid="back-to-hub"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] mb-5 hover:underline"
                style={{ color: G }}>
            <ArrowLeft className="w-4 h-4" /> All 12 Mini Lessons
          </Link>

          <div className="mb-5">
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: RED }}>
              ★ Mini Lesson #{tool.n} of 12
            </div>
            <h1 className="font-serif-display uppercase leading-[0.98] mt-1"
                style={{ color: INK, fontSize: "clamp(2rem, 4.5vw, 3.25rem)" }}>
              {tool.name}
            </h1>
            <p className="text-lg md:text-xl mt-2 italic" style={{ color: tool.color }}>
              {tool.tagline}
            </p>
          </div>

          <KineticLesson
            videoUrl={tool.video}
            title={tool.name}
            subtitle={`Mini Lesson #${tool.n} · 3 minutes`}
            duration={tool.duration}
            cards={tool.cards}
          />

          {/* Prev / Next pager */}
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {prev ? (
              <Link to={`/training/tool-intros/${prev.slug}`}
                    data-testid="prev-lesson"
                    className="group rounded-xl border-2 p-4 hover:shadow-md transition flex items-center gap-3"
                    style={{ borderColor: prev.color }}>
                <ChevronLeft className="w-7 h-7 flex-shrink-0 group-hover:-translate-x-1 transition" style={{ color: prev.color }} />
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold opacity-70" style={{ color: INK }}>
                    Previous · Lesson #{prev.n}
                  </div>
                  <div className="font-bold uppercase truncate" style={{ color: prev.color }}>{prev.name}</div>
                </div>
              </Link>
            ) : <div />}

            {next ? (
              <Link to={`/training/tool-intros/${next.slug}`}
                    data-testid="next-lesson"
                    className="group rounded-xl border-2 p-4 hover:shadow-md transition flex items-center justify-end gap-3 text-right"
                    style={{ borderColor: next.color }}>
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold opacity-70" style={{ color: INK }}>
                    Next · Lesson #{next.n}
                  </div>
                  <div className="font-bold uppercase truncate" style={{ color: next.color }}>{next.name}</div>
                </div>
                <ChevronRight className="w-7 h-7 flex-shrink-0 group-hover:translate-x-1 transition" style={{ color: next.color }} />
              </Link>
            ) : (
              <Link to="/training/tool-intros"
                    className="group rounded-xl border-2 p-4 hover:shadow-md transition flex items-center justify-end gap-3 text-right"
                    style={{ borderColor: G }}>
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold opacity-70" style={{ color: INK }}>
                    Finished!
                  </div>
                  <div className="font-bold uppercase" style={{ color: G }}>Back To All Lessons</div>
                </div>
                <ArrowRight className="w-7 h-7 flex-shrink-0 group-hover:translate-x-1 transition" style={{ color: G }} />
              </Link>
            )}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
